import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import SeoHead from '../common/SeoHead';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell, AreaChart, Area } from 'recharts';
import {
  Share2,
  Bookmark,
  BookmarkPlus,
  ChevronUp,
  Calendar,
  Clock,
  Tag,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Home,
  Sun,
  Zap,
  Building,
  Shield,
  BarChart2,
  Battery,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  ArrowRight,
  TrendingDown,
  Wallet,
  Factory,
  ShoppingBag,
  Users,
  Lightbulb,
  Plug
} from 'lucide-react';

const ElectricityPriceIncreaseSA2025 = () => {
  const { isDarkMode } = useTheme();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const publishDate = "2025-02-25T09:00:00Z";
  const articleRef = useRef(null);
  const siteUrl = 'https://solarinsight.co.za';
  const articlePath = '/news/electricity-price-increase-south-africa-2025';
  const fullUrl = `${siteUrl}${articlePath}`;

  // Handle scroll to top
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Electricity Price Increase in South Africa 2025: What You Need to Know',
          text: 'Learn about the rising cost of electricity in South Africa, and what it means for households and businesses.',
          url: fullUrl,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support navigator.share
      navigator.clipboard.writeText(fullUrl);
      alert('Link copied to clipboard!');
    }
  };

  // SEO data
  const seoData = {
    title: 'Electricity Price Increase in South Africa 2025: What You Need to Know | Solar Insight',
    description: 'Analysis of Eskom\'s 2025 electricity price hike in South Africa, how it impacts households and businesses, and why solar power offers a solution.',
    keywords: 'Eskom tariff increase, electricity price South Africa, NERSA, solar power alternative, load shedding, energy costs, electricity bills, 2025 electricity prices',
    path: articlePath,
    publishDate: publishDate,
    modifiedDate: publishDate,
    image: '/images/news/eskom-price-increase-2025.jpg',
    type: 'article',
    author: 'Solar Insight Team'
  };

  // Add new data for graphs
  const priceIncreaseData = [
    { year: '2020', price: 116.72 },
    { year: '2021', price: 134.30 },
    { year: '2022', price: 155.07 },
    { year: '2023', price: 184.04 },
    { year: '2024', price: 195.95 },
    { year: '2025', price: 220.92 },
    { year: '2026', price: 232.77 }, // Projected
    { year: '2027', price: 247.17 }, // Projected
  ];

  const sectorImpactData = [
    { sector: 'Manufacturing', impact: 65 },
    { sector: 'Mining', impact: 55 },
    { sector: 'Agriculture', impact: 45 },
    { sector: 'Retail', impact: 35 },
    { sector: 'Services', impact: 25 },
  ];

  const householdExpenseData = [
    { category: 'Average Home (500kWh)', before: 980, after: 1100 },
    { category: 'Large Home (800kWh)', before: 1568, after: 1760 },
    { category: 'Small Business', before: 3920, after: 4400 },
    { category: 'Medium Business', before: 7840, after: 8800 },
  ];

  const solarSavingsData = [
    { year: 1, withSolar: 1100, withoutSolar: 1320 },
    { year: 2, withSolar: 1100, withoutSolar: 1584 },
    { year: 3, withSolar: 1100, withoutSolar: 1900 },
    { year: 4, withSolar: 1100, withoutSolar: 2280 },
    { year: 5, withSolar: 1100, withoutSolar: 2736 },
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-gray-900'}`}>
      <SeoHead template="article" data={seoData} />
      
      <main className="container mx-auto px-4 py-8">
        <article ref={articleRef} className="max-w-4xl mx-auto">
          {/* Article Header */}
          <header className="mb-8">
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
              The Electricity Price Increase in South Africa: What You Need to Know
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm mb-6">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <time dateTime={publishDate}>
                  {new Date(publishDate).toLocaleDateString('en-ZA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>12 min read</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Tag className="h-4 w-4" />
                <span>Electricity Prices</span>
              </div>
            </div>
            
            <div className="flex gap-2 mb-6">
              <button
                onClick={handleShare}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm transition-colors ${
                  isDarkMode 
                    ? 'bg-blue-800 hover:bg-blue-700 text-white' 
                    : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                }`}
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
              
              <button
                onClick={() => setIsBookmarked(!isBookmarked)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm transition-colors ${
                  isDarkMode 
                    ? 'bg-blue-800 hover:bg-blue-700 text-white' 
                    : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                }`}
              >
                {isBookmarked ? (
                  <>
                    <Bookmark className="h-4 w-4" /> Bookmarked
                  </>
                ) : (
                  <>
                    <BookmarkPlus className="h-4 w-4" /> Bookmark
                  </>
                )}
              </button>
            </div>
            
            <div className={`p-4 rounded-lg mb-8 ${isDarkMode ? 'bg-blue-800/60' : 'bg-amber-100'}`}>
              <p className="text-sm md:text-base leading-relaxed">
                South Africans are facing yet another electricity price increase as the national utility Eskom raises tariffs in 2025. 
                This hike – approved by the energy regulator – comes amid ongoing load shedding and economic challenges. 
                In this article, we'll break down why Eskom electricity prices are increasing in South Africa, 
                how much more you'll be paying, and what it means for households and businesses. 
                We'll also explore solutions – including why many believe solar power is the future for affordable energy.
              </p>
            </div>
          </header>
          
          {/* Main Content */}
          <section className={`p-8 rounded-lg shadow-xl mb-12 ${
            isDarkMode ? 'bg-blue-800/50' : 'bg-amber-100/50'
          }`}>
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div>
                <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                  Understanding the 2025 Electricity Price Increase
                </h2>
                <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                  In January 2025, the National Energy Regulator of South Africa (NERSA) approved a significant 
                  electricity tariff hike for Eskom customers. Starting 1 April 2025, Eskom's average tariffs will 
                  increase by 12.74%. This is part of a multi-year determination: additional increases of 5.36% 
                  in 2026/27 and 6.19% in 2027/28 have also been authorised.
                </p>
              </div>
              
              {/* Price Trends Graph */}
              <section className={`my-8 p-6 rounded-lg ${isDarkMode ? 'bg-blue-800/50' : 'bg-amber-100/50'}`}>
                <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                  <TrendingUp className="inline-block mr-2 h-7 w-7" />
                  Electricity Price Trends (2020-2027)
                </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceIncreaseData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis 
                      label={{ 
                        value: 'Price (cents/kWh)', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: 10
                      }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-amber-900'}`}>
                              <p className="font-semibold">{`Year: ${label}`}</p>
                              <p>{`Price: ${payload[0].value.toFixed(2)} c/kWh`}</p>
                              {label >= '2026' && <p className="text-sm italic">*Projected</p>}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke={isDarkMode ? '#fbbf24' : '#f59e0b'} 
                      strokeWidth={2}
                      dot={{ fill: isDarkMode ? '#fbbf24' : '#f59e0b' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-amber-600'}`}>
                *Projections based on approved NERSA increases for 2026-2027
              </p>
            </section>

            <p>
              In January 2025, the National Energy Regulator of South Africa (NERSA) approved a significant 
              electricity tariff hike for Eskom customers. Starting 1 April 2025, Eskom's average tariffs will 
              increase by 12.74%. This is part of a multi-year determination: additional increases of 5.36% 
              in 2026/27 and 6.19% in 2027/28 have also been authorised.
            </p>
            
            {/* Monthly Bill Impact Section */}
            <h2>Impact on Households and Businesses</h2>
            
            <section className={`my-8 p-6 rounded-lg ${isDarkMode ? 'bg-blue-800/50' : 'bg-amber-100/50'}`}>
              <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                <Home className="inline-block mr-2 h-7 w-7" />
                Monthly Bill Comparison
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={householdExpenseData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis 
                      label={{ 
                        value: 'Monthly Bill (Rand)', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: 10
                      }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-amber-900'}`}>
                              <p className="font-semibold">{label}</p>
                              <p>{`Before: R${payload[0].value}`}</p>
                              <p>{`After: R${payload[1].value}`}</p>
                              <p className="text-sm mt-1">{`Increase: R${payload[1].value - payload[0].value}`}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="before" fill={isDarkMode ? '#60a5fa' : '#3b82f6'} name="Before Increase" />
                    <Bar dataKey="after" fill={isDarkMode ? '#fbbf24' : '#f59e0b'} name="After Increase" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-amber-600'}`}>
                The graph shows the impact of the 12.74% increase on different types of consumers
              </p>
            </section>

              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                In practical terms, the cost of electricity per kWh will rise from an average of about 195.95 cents 
                to 220.92 cents for direct Eskom customers. So if your household used 500 kWh in a month, for example, 
                you'd pay roughly R1100 instead of R980 – about R120 more due to this Eskom electricity price increase.
              </p>

              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Economic Impact on Different Sectors
              </h2>
            
              {/* Sector Impact Chart */}
              <section className={`my-8 p-6 rounded-lg ${isDarkMode ? 'bg-blue-800/50' : 'bg-amber-100/50'}`}>
                <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                  <Factory className="inline-block mr-2 h-7 w-7" />
                  Economic Impact by Sector
                </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectorImpactData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sector" />
                    <YAxis 
                      label={{ 
                        value: 'Impact on Operating Costs (%)', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: 10
                      }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-amber-900'}`}>
                              <p className="font-semibold">{label}</p>
                              <p>{`Cost Increase: ${payload[0].value}%`}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey="impact" 
                      fill={isDarkMode ? '#fbbf24' : '#f59e0b'}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
                <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-amber-600'}`}>
                  Manufacturing and mining sectors are most affected by the electricity price increase
                </p>
              </section>

              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Solar Power as a Solution
              </h2>
              
              {/* Solar Savings Projection */}
              <section className={`my-8 p-6 rounded-lg ${isDarkMode ? 'bg-blue-800/50' : 'bg-amber-100/50'}`}>
                <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                  <Sun className="inline-block mr-2 h-7 w-7" />
                  5-Year Cost Comparison: Solar vs Grid
                </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={solarSavingsData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" label={{ value: 'Years', position: 'bottom' }} />
                    <YAxis 
                      label={{ 
                        value: 'Monthly Cost (Rand)', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: 10
                      }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const savings = payload[1].value - payload[0].value;
                          return (
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-amber-900'}`}>
                              <p className="font-semibold">{`Year ${label}`}</p>
                              <p>{`With Solar: R${payload[0].value}`}</p>
                              <p>{`Grid Only: R${payload[1].value}`}</p>
                              <p className="text-sm mt-1 font-semibold">{`Monthly Savings: R${savings}`}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="withSolar" 
                      stackId="1" 
                      stroke={isDarkMode ? '#fbbf24' : '#f59e0b'} 
                      fill={isDarkMode ? '#fbbf24' : '#f59e0b'} 
                      name="With Solar"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="withoutSolar" 
                      stackId="2" 
                      stroke={isDarkMode ? '#60a5fa' : '#3b82f6'} 
                      fill={isDarkMode ? '#60a5fa' : '#3b82f6'} 
                      name="Grid Only"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
                <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-blue-700/50' : 'bg-amber-200/50'}`}>
                  <div className="flex items-center gap-2">
                    <Lightbulb className={`h-5 w-5 ${isDarkMode ? 'text-yellow-400' : 'text-amber-500'}`} />
                    <p className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      Solar systems typically pay for themselves within 4-7 years through electricity savings.
                    </p>
                  </div>
                </div>
              </section>
            
              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Why Are Electricity Prices Increasing in South Africa?
              </h2>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                Many consumers are asking why electricity prices are increasing in South Africa so dramatically. 
                The reasons are a complex mix of financial, operational, and historical factors:
              </p>
              
              <div className={`p-6 rounded-lg mb-8 ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                <ul className="space-y-4">
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Eskom's Financial Crisis:</strong> Years of mismanagement, corruption, and cost overruns 
                      have left Eskom in dire straits. The utility has amassed hundreds of billions of rand in debt and 
                      has struggled to cover its operating costs.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>High Fuel and Maintenance Costs:</strong> Eskom relies heavily on an aging fleet of coal-fired 
                      power stations, many of which are unreliable and inefficient. Breakdowns and maintenance backlogs 
                      drive up operating costs. To keep the lights on during breakdowns, Eskom has burned vast amounts of 
                      diesel in auxiliary power plants – an extremely expensive way to generate electricity.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Recovering Previous Losses:</strong> Eskom argues that it needs large increases now to "catch up" 
                      for revenue it didn't get in earlier years. When regulators denied Eskom's full requested hikes in the past, 
                      the utility's costs didn't disappear.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Municipal Debt and Non-Payment:</strong> Another often-cited factor is that many municipalities 
                      (which buy bulk power from Eskom to resell to residents) owe Eskom large sums of money. Electricity theft 
                      and non-payment by end-users also contribute to revenue losses.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Inflation and Infrastructure Investment:</strong> General inflation affects costs too – wages, 
                      materials, and maintenance become pricier each year. The approved 12.74% increase for 2025 is about 
                      triple the current inflation rate (around 3-5%), highlighting deeper issues.
                    </div>
                  </li>
                </ul>
              </div>
            
              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                The Impact on Households and Businesses
              </h2>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                A double-digit electricity tariff increase has far-reaching implications for South Africans:
              </p>
              
              <div className={`p-6 rounded-lg mb-8 ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                <ul className="space-y-4">
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Household Budgets Under Strain:</strong> Consumers are already grappling with a cost-of-living crisis. 
                      For many families, electricity is a significant monthly expense, and a 12.7% increase can force cutbacks elsewhere.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Inflation and Cost-Push Effects:</strong> When tariffs go up, businesses face higher operating 
                      costs. This can feed through to consumer prices in general – from goods on store shelves to services – 
                      further fuelling inflation.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Pressure on Businesses and Jobs:</strong> Energy-intensive industries are especially hard-hit. 
                      A recent example is ArcelorMittal's South African steel operations – the company announced closure 
                      citing unsustainable electricity costs, putting up to 100,000 jobs at risk.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Daily Life and Quality of Living:</strong> South Africans endure scheduled power cuts regularly; 
                      paying more for electricity when you can't even get 24/7 supply feels like adding insult to injury.
                    </div>
                  </li>
                </ul>
              </div>
            
              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Opposition and Public Reaction
              </h2>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                The announcement of another electricity price hike provoked strong reactions across South Africa:
              </p>
              
              <div className={`p-6 rounded-lg mb-8 ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                <ul className="space-y-4">
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Political Opposition:</strong> Virtually all major opposition parties rejected the tariff increase. 
                      The Democratic Alliance (DA) slammed the 12.74% hike as "simply unacceptable," arguing that citizens are 
                      being forced to pay for Eskom's inefficiencies and failures.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Public Outrage:</strong> During NERSA's public hearings on the tariff application, citizens turned 
                      out in large numbers to object. Social media platforms saw hashtags like #EskomTariffs and #ElectricityHike trend, 
                      as users vented about "paying more for darkness."
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>"We Can't Afford This":</strong> From taxi drivers to shop owners to pensioners, many South Africans 
                      are genuinely worried about how they will absorb the higher electricity costs. Some have said they might have 
                      to disconnect appliances like geysers or limit electric heating in winter.
                    </div>
                  </li>
                </ul>
              </div>
            
              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                The Role of Renewable Energy: Why Solar Power is the Future
              </h2>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                With Eskom electricity becoming costlier and often unavailable due to load shedding, South Africans are 
                increasingly looking to renewable energy solutions:
              </p>
              
              <div className={`p-6 rounded-lg mb-8 ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                <ul className="space-y-4">
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Abundant Sunshine and Improving Technology:</strong> South Africa is blessed with some of the 
                      highest solar irradiation levels in the world. Solar PV module prices have been dropping by around 10% 
                      or more each year as technology advances and manufacturing scales up.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Cost-Effectiveness:</strong> While Eskom's grid tariffs keep rising every year, the cost of 
                      generating your own electricity from solar remains fixed after installation. According to energy 
                      analysts, solar can already produce electricity at a fraction of Eskom's price – less than R1.00/kWh, 
                      compared to Eskom's approaching R4/kWh.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Energy Independence and Reliability:</strong> Rooftop solar systems coupled with batteries can 
                      keep a home or business running through grid outages. This reliability factor has been a huge driver of 
                      the recent solar boom.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Booming Adoption:</strong> South Africa is already in the midst of a solar revolution. Eskom's 
                      own data suggests that rooftop solar capacity more than quintupled in two years – from under 1 GW in 2021 
                      to over 4 GW by mid-2023.
                    </div>
                  </li>
                  
                  <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                    <div>
                      <strong>Environmental Benefits:</strong> By switching to solar, households and companies reduce their 
                      carbon footprint since they draw less from coal-based electricity. Every kilowatt of solar helps move 
                      the nation toward a cleaner, more sustainable energy mix.
                    </div>
                  </li>
                </ul>
              </div>
            
              <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    Why have electricity prices increased in South Africa?
                  </h3>
                  <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    Electricity prices are rising mainly due to Eskom's financial and operational problems. The utility has 
                    huge debts, costly maintenance issues, and fuel expenses that have accumulated over years of poor management. 
                    Essentially, Eskom needs more revenue to cover its costs.
                  </p>
                </div>
                
                <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    How much does Eskom charge per kWh in 2025?
                  </h3>
                  <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    With the new tariff increase, the average standard tariff Eskom will charge is around R2.20 per kWh in 2025. 
                    Before the increase, the average Eskom tariff was roughly R1.96/kWh, and a 12.74% hike brings it to about 
                    220.92 cents per kWh (excluding VAT) as of April 1, 2025.
                  </p>
                </div>
                
                <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    What are the best ways to reduce electricity costs?
                  </h3>
                  <p className={`text-lg leading-relaxed mb-4 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    The most effective strategies include:
                  </p>
                  <ul className="space-y-2">
                    <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                      <span>Improving energy efficiency with LED bulbs and efficient appliances</span>
                    </li>
                    <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                      <span>Conserving energy by changing habits and being mindful of usage</span>
                    </li>
                    <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                      <span>Switching to alternatives for heating (solar geysers, heat pumps)</span>
                    </li>
                    <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                      <span>Investing in solar power and battery backup systems</span>
                    </li>
                    <li className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'}`}></div>
                      <span>Monitoring and managing your usage with smart meters</span>
                    </li>
                  </ul>
                </div>
                
                <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-700/30' : 'bg-amber-100'}`}>
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    Is solar power a cost-effective alternative to Eskom electricity?
                  </h3>
                  <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                    Yes, solar power is now very cost-effective compared to Eskom supply, especially over the long term. 
                    A well-designed solar installation often pays for itself within 4-7 years. After that, the electricity 
                    it produces is essentially free apart from minor maintenance. With panels lasting 20+ years, this 
                    represents an excellent return on investment.
                  </p>
                </div>
              </div>
              
              <h2 className={`text-3xl font-bold mb-6 mt-8 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                Conclusion
              </h2>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                South Africa's electricity price increases show no sign of abating, and the challenges at Eskom are 
                deeply entrenched. Rather than remaining at the mercy of rising tariffs and rolling blackouts, consumers 
                have the option to take charge of their energy future.
              </p>
              
              <p className={`text-xl leading-relaxed mb-6 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
                By investing in solutions like solar power, battery backup, and efficiency upgrades, you can future-proof 
                your home or business against Eskom's woes. Going solar not only slashes your electricity bills but also 
                provides reliability and peace of mind in these uncertain times.
              </p>
            </div>
          </section>
          
          {/* Call to Action Section */}
          <section className={`my-8 p-6 rounded-lg ${isDarkMode ? 'bg-blue-800' : 'bg-amber-100'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
              Take Action Now
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-100' : 'text-amber-900'}`}>
              Don't wait until the next tariff hike or the next bout of load shedding. Take the first step toward 
              energy independence – reach out to a reputable solar energy provider for an assessment.
            </p>
            <div className="flex justify-center">
              <Link 
                to="/contact"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' 
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                <span className="font-semibold">Explore Solar Solutions</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </section>
          
          {/* Related Articles Section */}
          <section className="mb-12">
            <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/news/stage-6-loadshedding-south-africa"
                className={`p-4 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'bg-blue-800 hover:bg-blue-700' : 'bg-amber-100 hover:bg-amber-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    Stage 6 Loadshedding Crisis in South Africa
                  </h3>
                  <ArrowRight className={`h-5 w-5 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`} />
                </div>
                <p className="text-sm mt-2">February 15, 2025</p>
              </Link>
              
              <Link
                to="/news/solar-registration-laws-2025"
                className={`p-4 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'bg-blue-800 hover:bg-blue-700' : 'bg-amber-100 hover:bg-amber-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    South Africa's New Solar Registration Laws
                  </h3>
                  <ArrowRight className={`h-5 w-5 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`} />
                </div>
                <p className="text-sm mt-2">February 10, 2025</p>
              </Link>
            </div>
          </section>
          
          {/* Scroll to Top Button */}
          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 ${
                isDarkMode
                  ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              }`}
            >
              <ChevronUp className="h-6 w-6" />
            </button>
          )}
        </article>
      </main>
    </div>
  );
};

export default ElectricityPriceIncreaseSA2025; 